import { Budget } from "@interfaces/budget-response";
import { SimpleItem } from "@interfaces/item";
import { Modal } from "@interfaces/modal";
import { NfePayload } from "@interfaces/nfe-payload";
import { ServicoSelecionado } from "@interfaces/service";
import { createFeature, createReducer, on } from "@ngrx/store";
import { AppActions } from "../actions/app.actions";

export const appFeatureKey = "app";

export interface State {
  inProgressRequests: string[];
  modais: Modal[];
  selectedBudget: Budget | null;
  selectedItem: SimpleItem | null;
  lastNfeData: NfePayload | null;
  selectedServices: ServicoSelecionado[];
}

export const initialState: State = {
  inProgressRequests: [],
  modais: [],
  selectedBudget: null,
  selectedItem: null,
  lastNfeData: null,
  selectedServices: []
};

export const reducer = createReducer(
  initialState,
  on(
    AppActions.queueRequest,
    (state, { id }): State => ({
      ...state,
      inProgressRequests: state.inProgressRequests.concat(id)
    })
  ),
  on(
    AppActions.resolveRequest,
    (state, { id }): State => ({
      ...state,
      inProgressRequests: state.inProgressRequests.filter((req) => req !== id)
    })
  ),
  on(
    AppActions.queueModal,
    (state, { modal }): State => ({
      ...state,
      modais: state.modais.concat(modal)
    })
  ),
  on(
    AppActions.removeModal,
    (state, { id }): State => ({
      ...state,
      modais: state.modais.filter((m) => m.id !== id)
    })
  ),
  on(
    AppActions.selectBudget,
    (state, { budget }): State => ({
      ...state,
      selectedBudget: budget
    })
  ),
  on(
    AppActions.selectBudgetForNfse,
    (state, { budget }): State => ({
      ...state,
      selectedBudget: budget,
      // Emissão nova a partir da obra: não herda serviços de uma emissão
      // anterior abandonada.
      selectedServices: []
    })
  ),
  on(
    AppActions.selectBudgetForNfce,
    (state, { budget }): State => ({
      ...state,
      selectedBudget: budget
    })
  ),
  on(
    AppActions.clearSelectedBudget,
    (state): State => ({
      ...state,
      selectedBudget: null
    })
  ),
  on(
    AppActions.selectItemToEdit,
    (state, { item }): State => ({
      ...state,
      selectedItem: item
    })
  ),
  on(
    AppActions.clearSelectedItem,
    (state): State => ({
      ...state,
      selectedItem: null
    })
  ),
  on(
    AppActions.cacheNfe,
    (state, { payload }): State => ({
      ...state,
      lastNfeData: payload
    })
  ),
  on(
    AppActions.clearNfeCache,
    (state): State => ({
      ...state,
      lastNfeData: null
    })
  ),
  on(AppActions.addService, (state, { service }): State => {
    // O mesmo serviço não pode entrar duas vezes na nota: a quantidade é dada
    // pelo valor do item, não por itens repetidos. Sem esta guarda, voltar da
    // emissão e selecionar o serviço de novo duplicava o card.
    if (state.selectedServices.some((selected) => selected.id === service.id)) {
      return state;
    }

    return {
      ...state,
      // O valor não vem do cadastro: nasce vazio e é preenchido na emissão.
      selectedServices: [
        ...state.selectedServices,
        { ...service, valor: { servico: null } }
      ]
    };
  }),
  on(
    AppActions.updateServiceValue,
    (state, { serviceId, valor }): State => ({
      ...state,
      selectedServices: state.selectedServices.map((s) =>
        s.id === serviceId ? { ...s, valor: { servico: valor } } : s
      )
    })
  ),
  on(
    AppActions.updateServiceDiscriminacao,
    (state, { serviceId, discriminacao }): State => ({
      ...state,
      selectedServices: state.selectedServices.map((s) =>
        s.id === serviceId ? { ...s, discriminacao } : s
      )
    })
  ),
  on(
    AppActions.removeService,
    (state, { serviceId }): State => ({
      ...state,
      selectedServices: state.selectedServices.filter(
        (s) => s.id !== serviceId
      )
    })
  ),
  on(
    AppActions.clearSelectedServices,
    (state): State => ({
      ...state,
      selectedServices: []
    })
  )
);

export const appFeature = createFeature({
  name: appFeatureKey,
  reducer
});
